'use client';

import React, { FC, useMemo, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { productsInSkuOrder } from 'helpers/algolia/product-ids';
import ProductTile from '../product-tile';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export type ProductListCMSProps = {
  /** Server-fetched Algolia hits (backend action → Redis). See the renovation-guide tastic. */
  hits: any[];
  /** The item-codes the CMS entry listed, in the author's order. */
  skuCodes: string[];
};

/** How many products are shown before the "Show all products" button. */
const INITIAL_PRODUCTS = 4;

/**
 * The renovation-guide product grid. A pure view: its products are fetched on the SERVER (see
 * frontastic/tastics/products/product-list-cms), so nothing here queries Algolia and the tiles are in
 * the SSR HTML. Ordering and variant selection are the shared `productsInSkuOrder` helper.
 */
const ProductListCMS: FC<ProductListCMSProps> = ({ hits, skuCodes }) => {
  const [productsDisplayed, setProductsDisplayed] = useState<number>(INITIAL_PRODUCTS);
  const { locale } = useParams();
  const currentURL = usePathname();
  const isProductDetailPage = isPdpUrl(currentURL);

  const products = useMemo(() => productsInSkuOrder(hits, skuCodes, locale as string), [hits, skuCodes, locale]);

  return (
    <div
      id="related-products"
      className={`our-product-container py-48 px-24 xl:pb-80 2xl:pb-80 md:py-48 2xl:pt-48 ${
        isProductDetailPage
          ? 'relative left-full w-screen -translate-x-full bg-[#EDEDED] px-48 md:pb-1 md:pt-100 lg:grid lg:grid-cols-12 lg:pl-0 xl:pb-153 xl:pt-153'
          : 'md:px-48'
      }`}
    >
      <div className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4' : ''}>
        <div className="m-auto grid w-[min(100%,47rem)] grid-cols-2 gap-8">
          {products.slice(0, productsDisplayed).map((product, index) => (
            <div key={product.productId ?? product.slug ?? index}>
              <ProductTile product={product} layout="compact" />
            </div>
          ))}
        </div>
        {products.length > productsDisplayed && (
          <div className="mt-4 text-center">
            <button className="mt-10 bg-[#2B2C39] px-25 py-13 text-white" onClick={() => setProductsDisplayed(products.length)}>
              Show all products
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductListCMS;
